import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarRange, ListTodo, Gavel, LayoutDashboard, Settings as SettingsIcon,
  CreditCard, Users, Sun, Moon, Search, CornerDownLeft, LineChart, Plus, LogOut,
} from "lucide-react";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import api from "@/lib/api";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";

const NAV_COMMANDS = [
  { id: "nav-dash", label: "Go to Dashboard", to: "/", icon: LayoutDashboard, kw: "home overview" },
  { id: "nav-meet", label: "Go to Meetings", to: "/meetings", icon: CalendarRange, kw: "calls sessions" },
  { id: "nav-task", label: "Go to Tasks", to: "/tasks", icon: ListTodo, kw: "action items kanban" },
  { id: "nav-dec", label: "Go to Decisions", to: "/decisions", icon: Gavel, kw: "register approvals" },
  { id: "nav-ins", label: "Go to Insights", to: "/insights", icon: LineChart, kw: "analytics reports" },
  { id: "nav-bill", label: "Go to Billing", to: "/billing", icon: CreditCard, kw: "plan invoice gst" },
  { id: "nav-set", label: "Go to Settings", to: "/settings", icon: SettingsIcon, kw: "profile account" },
];

export const CommandPalette = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const [q, setQ] = useState("");
  const [data, setData] = useState({ meetings: [], tasks: [], decisions: [] });
  const loadedRef = useRef(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  useEffect(() => {
    if (!open || loadedRef.current) return;
    loadedRef.current = true;
    Promise.all([
      api.get("/meetings").then((r) => r.data).catch(() => []),
      api.get("/tasks").then((r) => r.data).catch(() => []),
      api.get("/ac/decisions").then((r) => r.data).catch(() => []),
    ]).then(([meetings, tasks, decisions]) => {
      setData({
        meetings: Array.isArray(meetings) ? meetings.slice(0, 200) : [],
        tasks: Array.isArray(tasks) ? tasks.slice(0, 200) : [],
        decisions: Array.isArray(decisions) ? decisions.slice(0, 200) : [],
      });
    });
  }, [open]);

  const run = (fn) => { setOpen(false); setQ(""); setTimeout(fn, 0); };

  const needle = q.trim().toLowerCase();
  const match = (s) => !needle || String(s || "").toLowerCase().includes(needle);

  const meetings = (data.meetings || []).filter((m) => match(m.title) || match(m.meeting_date)).slice(0, 6);
  const tasks = (data.tasks || []).filter((t) => match(t.title)).slice(0, 6);
  const decisions = (data.decisions || []).filter((d) => match(d.decision_text) || match(d.title)).slice(0, 6);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <div data-testid="command-palette" className="border-b border-border">
        <CommandInput
          data-testid="command-palette-input"
          placeholder="Search meetings, tasks, decisions — or type a command…"
          value={q}
          onValueChange={setQ}
        />
      </div>
      <CommandList className="yl-scroll max-h-[420px]">
        <CommandEmpty>
          <div className="py-8 text-center">
            <Search className="w-5 h-5 mx-auto text-muted-foreground" strokeWidth={1.6} />
            <p className="mt-3 text-sm text-muted-foreground">No matches. Try a different term.</p>
          </div>
        </CommandEmpty>

        {meetings.length > 0 && (
          <CommandGroup heading="Meetings">
            {meetings.map((m) => (
              <CommandItem
                key={m.id}
                value={`meeting ${m.title} ${m.meeting_date}`}
                data-testid={`cmd-meeting-${m.id}`}
                onSelect={() => run(() => navigate(`/meetings/${m.id}`))}
                className="gap-3"
              >
                <CalendarRange className="w-4 h-4 text-primary shrink-0" strokeWidth={1.8} />
                <span className="truncate flex-1">{m.title}</span>
                <span className="font-mono text-[10px] text-muted-foreground shrink-0">{m.meeting_date}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {tasks.length > 0 && (
          <CommandGroup heading="Tasks">
            {tasks.map((t) => (
              <CommandItem
                key={t.id}
                value={`task ${t.title}`}
                data-testid={`cmd-task-${t.id}`}
                onSelect={() => run(() => navigate("/tasks"))}
                className="gap-3"
              >
                <ListTodo className="w-4 h-4 text-primary shrink-0" strokeWidth={1.8} />
                <span className="truncate flex-1">{t.title}</span>
                <span className="font-mono text-[10px] text-muted-foreground shrink-0">{t.status}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {decisions.length > 0 && (
          <CommandGroup heading="Decisions">
            {decisions.map((d) => (
              <CommandItem
                key={d.id}
                value={`decision ${d.decision_text || d.title}`}
                data-testid={`cmd-decision-${d.id}`}
                onSelect={() => run(() => navigate("/decisions"))}
                className="gap-3"
              >
                <Gavel className="w-4 h-4 text-primary shrink-0" strokeWidth={1.8} />
                <span className="truncate flex-1">{d.decision_text || d.title}</span>
                <span className="font-mono text-[10px] text-muted-foreground shrink-0">{d.workflow_status || ""}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandSeparator />

        <CommandGroup heading="Navigate">
          {NAV_COMMANDS.map((c) => (
            <CommandItem
              key={c.id}
              value={`${c.label} ${c.kw}`}
              data-testid={`cmd-${c.id}`}
              onSelect={() => run(() => navigate(c.to))}
              className="gap-3"
            >
              <c.icon className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.8} />
              <span className="flex-1">{c.label}</span>
              <CornerDownLeft className="w-3 h-3 text-muted-foreground/60" strokeWidth={1.8} />
            </CommandItem>
          ))}
          {user?.role === "Admin" && (
            <CommandItem
              value="Go to workspaces and users admin"
              data-testid="cmd-nav-admin"
              onSelect={() => run(() => navigate("/admin/users"))}
              className="gap-3"
            >
              <Users className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.8} />
              <span className="flex-1">Go to Workspaces &amp; users</span>
            </CommandItem>
          )}
        </CommandGroup>

        <CommandGroup heading="Actions">
          <CommandItem
            value="new meeting create schedule"
            data-testid="cmd-new-meeting"
            onSelect={() => run(() => navigate("/meetings?new=1"))}
            className="gap-3"
          >
            <Plus className="w-4 h-4 text-primary shrink-0" strokeWidth={2} />
            <span className="flex-1">Create a new meeting</span>
          </CommandItem>
          <CommandItem
            value="toggle theme dark light appearance"
            data-testid="cmd-toggle-theme"
            onSelect={() => run(toggle)}
            className="gap-3"
          >
            {theme === "dark"
              ? <Sun className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.8} />
              : <Moon className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.8} />}
            <span className="flex-1">Switch to {theme === "dark" ? "light" : "dark"} mode</span>
          </CommandItem>
          <CommandItem
            value="sign out logout"
            data-testid="cmd-sign-out"
            onSelect={() => run(logout)}
            className="gap-3"
          >
            <LogOut className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.8} />
            <span className="flex-1">Sign out</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
